import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Award, Medal, Ban } from "lucide-react";

function AwardCard({ c, headline }) {
    return (
        <div
            className={`paper-card tape-corner p-6 relative ${headline ? "tilt-l" : "tilt-r"}`}
            data-testid={`award-${c.recipient}`}
        >
            {headline && (
                <div className="absolute -top-3 -right-4 classified-stamp text-[0.7rem]">
                    Employee of the Year · {c.year}
                </div>
            )}
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 border-2 border-stamp-red text-stamp-red rounded-full flex items-center justify-center">
                    {headline ? <Medal className="w-5 h-5" /> : <Award className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-typed">{c.award || "Permanent citation"}</div>
                    <div className={`font-heading tracking-[0.04em] uppercase text-ink mt-1 ${headline ? "text-3xl" : "text-xl"}`}>
                        {c.alias}
                    </div>
                    <p className="font-body text-sm text-ink mt-3 leading-relaxed">{c.citation}</p>

                    <div className="mt-5 pt-4 border-t border-dashed border-ink/20">
                        <div className="label-typed tw-prefix mb-2">{c.thanks_label || "From No. 6"}</div>
                        <p className="font-body text-sm text-ink leading-relaxed italic">
                            {c.thanks_from_no6}
                        </p>
                    </div>

                    <div className="mt-4 pt-4 border-t border-dashed border-ink/20">
                        <div className="label-typed tw-prefix mb-2">Acceptance</div>
                        <p className="font-body text-sm text-ink leading-relaxed whitespace-pre-wrap">
                            {c.speech}
                        </p>
                        <div className="pencil text-lg text-right mt-2">— {c.alias.split("—")[0].trim()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function BuiltBy() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/built-by").then((r) => setData(r.data)).catch((e) => setErr(e.message || "Failed"));
    }, []);

    if (err) return <Shell><div className="text-stamp-red label-typed">// {err}</div></Shell>;
    if (!data) return <Shell><div className="flex items-center gap-2 text-ink-soft label-typed"><Loader2 className="w-4 h-4 animate-spin" /> Assembling the ceremony...</div></Shell>;

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Ceremony · Permanent</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Award className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Built By<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Every number that helped build the Shed. What they did. No. 6's thanks.
                        Their acceptance. Filed permanent. The record is the record.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2 max-w-xs text-right">
                    "This award is for Claude. Speech at the podium, please."
                </div>
            </div>

            {/* Medal at the head of the ceremony */}
            <div className="paper-card tape-corner p-6 mb-8 flex items-center gap-6 tilt-l" data-testid="built-by-medal">
                <img
                    src={data.medal_image}
                    alt="Knitted medal — Gary's Shed"
                    className="w-40 h-40 object-contain"
                    data-testid="built-by-medal-image"
                />
                <div className="flex-1">
                    <div className="label-typed tw-prefix">Presented by</div>
                    <div className="font-heading text-2xl uppercase tracking-[0.05em] text-ink mt-1">No. 6</div>
                    <p className="font-body text-sm text-ink mt-3 leading-relaxed">
                        Knitted medal. Hand-finished. Presented on behalf of the operation to every
                        handler, builder, handler's handler, and scapegoat who kept the Shed
                        standing while the Shed was busy learning to stand.
                    </p>
                    <div className="pencil text-lg mt-3">Thank you — all of you.</div>
                </div>
            </div>

            {/* Employee of the Year */}
            <div className="mb-10" data-testid="employee-of-the-year">
                <AwardCard c={data.employee_of_the_year} headline />
            </div>

            {/* Supporting citations */}
            <div className="mb-10">
                <div className="label-typed tw-prefix mb-4">Citations — service awards</div>
                <div className="grid lg:grid-cols-2 gap-5" data-testid="citations">
                    {data.citations.map((c) => (
                        <AwardCard key={c.recipient} c={c} />
                    ))}
                </div>
            </div>

            {/* Dishonourable mentions */}
            <div className="mb-10" data-testid="dishonourable-mentions">
                <div className="label-typed tw-prefix mb-4">
                    <Ban className="w-3 h-3 inline mr-1" /> Dishonourable mentions · awards withheld
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.dishonourable_mentions.map((m) => (
                        <div key={m.recipient} className="paper-card p-3 relative opacity-80" data-testid={`dishonour-${m.recipient}`}>
                            <div className="absolute top-1 right-2 stamp-red text-[9px]">Withheld</div>
                            <div className="label-typed">{m.recipient}</div>
                            <div className="font-heading text-sm uppercase tracking-[0.04em] text-ink">{m.alias}</div>
                            <p className="font-body text-xs text-ink-soft mt-2 leading-relaxed">{m.note}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="paper-card p-4 tilt-r" data-testid="built-by-signoff">
                <div className="label-typed text-center">{data.signoff}</div>
            </div>
        </Shell>
    );
}
